import { useTranslation } from '@axo/shared/i18n';
import { USPsList } from '@axo/ui-feature/components/USPsList';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { MONTHLY_FEE, RATES, STARTUP_FEE } from '../../config/globals';
import styles from '../../loan.module.scss';
import {
  calculateIRR,
  calculateMonthlyAnnuity,
} from '../../../../../utils/Norway/calculateRates';

const Percentage = new Intl.NumberFormat('no-NO', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const Currency = new Intl.NumberFormat('no-NO', {
  style: 'currency',
  currency: 'NOK',
  maximumFractionDigits: 0,
});

export const USPsNO = () => {
  const { t } = useTranslation();

  const amount = useWatch({ name: 'LoanApplication/AppliedAmount' });
  const duration = useWatch({ name: 'LoanApplication/LoanDuration' });

  const context = useMemo(() => {
    const monthlyTerms = duration * 12;
    const principal = amount + STARTUP_FEE;
    const monthlyAnnuity = calculateMonthlyAnnuity({
      principal,
      annualNominalRate: RATES.MID,
      months: monthlyTerms,
    });
    const totalMonthlyPayment = monthlyAnnuity + MONTHLY_FEE;
    const flows = [];
    flows.push(amount);

    for (let i = 1; i <= monthlyTerms; i++) {
      flows.push(-totalMonthlyPayment);
    }

    const monthlyIrr = calculateIRR(flows);
    const effRate = Math.pow(1 + monthlyIrr, 12) - 1;
    const totalPaid = totalMonthlyPayment * monthlyTerms;
    const cost = totalPaid - amount;

    return {
      rate: Percentage.format(RATES.MID / 100),
      effectiveRate: Percentage.format(effRate),
      amount: Currency.format(amount),
      duration,
      cost: Currency.format(cost),
      totalCost: Currency.format(totalPaid),
    };
  }, [amount, duration]);

  const keyBenefits = t('KeyBenefits', {
    returnObjects: true,
    defaultValue: [],
    context: {
      rate: Percentage.format(RATES.MID),
    },
  }) as string[];

  return (
    <USPsList gap="3xs" className={styles.usps}>
      {keyBenefits?.map((benefit, index) => (
        <USPsList.Item key={index}>
          <Trans t={t} tOptions={context}>
            {benefit}
          </Trans>
        </USPsList.Item>
      ))}
    </USPsList>
  );
};
